import { useState, useEffect } from "react";
import { useGetAdminQuizDifficultiesQuery, useUpdateAdminQuizDifficultyMutation, useCreateAdminQuizDifficultyMutation, useDeactivateAdminQuizDifficultyMutation } from "../../../lib/apollo/graphql/quiz/quiz-generated";
import PageWrapper from "../../common/components/PageWrapper";
import BasicButton from "../../common/components/BasicButton";
import { formatDate } from "../../../utilities/format";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bars3Icon, InformationCircleIcon } from "@heroicons/react/24/outline";

interface Difficulty {
  id: string;
  code: string;
  label: string;
  points: number | string;
  order: number;
  isActive: boolean;
  updatedAt: string;
}

interface SortableRowProps {
  difficulty: Difficulty;
  editingCell: { id: string; field: "points" | "label" | "code" } | null;
  onCellDoubleClick: (id: string, field: "points" | "label" | "code") => void;
  onCellChange: (id: string, field: "points" | "label" | "code", value: string) => void;
  onCellBlur: () => void;
  onToggleActive: (id: string) => void;
  isNewRow: boolean;
}

function SortableRow({ difficulty, editingCell, onCellDoubleClick, onCellChange, onCellBlur, onToggleActive, isNewRow }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: difficulty.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex w-fit border-b border-zinc-300 bg-white hover:bg-gray-50"
    >
      <div
        className="flex items-center justify-center border-e px-2 py-3 cursor-grab active:cursor-grabbing"
        style={{ width: "80px", minWidth: "80px" }}
        {...attributes}
        {...listeners}
      >
        <Bars3Icon className="w-5 h-5 text-gray-400" />
      </div>

      <div
        className="flex items-center justify-center border-e px-2 py-3 cursor-pointer hover:bg-gray-50"
        style={{ width: "140px", minWidth: "140px" }}
        onDoubleClick={() => onCellDoubleClick(difficulty.id, "code")}
      >
        {editingCell?.id === difficulty.id && editingCell?.field === "code" ? (
          <input
            type="text"
            value={difficulty.code}
            onChange={(e) => onCellChange(difficulty.id, "code", e.target.value)}
            onBlur={onCellBlur}
            autoFocus
            className="w-full px-2 py-1 border border-gray-300 rounded text-center font-mono text-sm"
          />
        ) : (
          <span className="font-mono text-sm">{difficulty.code}</span>
        )}
      </div>

      <div
        className="flex items-center justify-center border-e px-2 py-3 cursor-pointer hover:bg-gray-50"
        style={{ width: "180px", minWidth: "180px" }}
        onDoubleClick={() => onCellDoubleClick(difficulty.id, "label")}
      >
        {editingCell?.id === difficulty.id && editingCell?.field === "label" ? (
          <input
            type="text"
            value={difficulty.label}
            onChange={(e) => onCellChange(difficulty.id, "label", e.target.value)}
            onBlur={onCellBlur}
            autoFocus
            className="w-full px-2 py-1 border border-gray-300 rounded text-center"
          />
        ) : (
          <span>{difficulty.label}</span>
        )}
      </div>

      <div
        className="flex items-center justify-center border-e px-2 py-3 cursor-pointer hover:bg-gray-50"
        style={{ width: "120px", minWidth: "120px" }}
        onDoubleClick={() => onCellDoubleClick(difficulty.id, "points")}
      >
        {editingCell?.id === difficulty.id && editingCell?.field === "points" ? (
          <input
            type="number"
            value={difficulty.points}
            onChange={(e) => onCellChange(difficulty.id, "points", e.target.value)}
            onBlur={onCellBlur}
            onFocus={(e) => e.target.select()}
            autoFocus
            className="w-full px-2 py-1 border border-gray-300 rounded text-center"
          />
        ) : (
          <span>{difficulty.points}점</span>
        )}
      </div>

      <div
        className="flex items-center justify-center border-e px-2 py-3 cursor-pointer hover:bg-gray-50"
        style={{ width: "80px", minWidth: "80px" }}
        onClick={() => onToggleActive(difficulty.id)}
      >
        <span className={`text-xs px-2 py-1 rounded ${difficulty.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {difficulty.isActive ? "활성" : "비활성"}
        </span>
      </div>

      <div
        className="flex items-center justify-center border-e px-2 py-3 text-sm"
        style={{ width: "120px", minWidth: "120px" }}
      >
        {isNewRow ? "-" : formatDate(difficulty.updatedAt, "YY.MM.DD")}
      </div>

    </div>
  );
}

export default function QuizDifficultyContainer() {
  const { data, loading, refetch } = useGetAdminQuizDifficultiesQuery({
    variables: { includeInactive: true }
  });
  const [updateDifficulty] = useUpdateAdminQuizDifficultyMutation();
  const [createDifficulty] = useCreateAdminQuizDifficultyMutation();
  const [deactivateDifficulty] = useDeactivateAdminQuizDifficultyMutation();

  const [difficulties, setDifficulties] = useState<Difficulty[]>([]);
  const [originalDifficulties, setOriginalDifficulties] = useState<Difficulty[]>([]);
  const [editingCell, setEditingCell] = useState<{ id: string; field: "points" | "label" | "code" } | null>(null);
  const [newRows, setNewRows] = useState<Set<string>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (data?.difficulties) {
      const sorted = [...data.difficulties].sort((a, b) => a.order - b.order);
      setDifficulties(sorted as Difficulty[]);
      setOriginalDifficulties(JSON.parse(JSON.stringify(sorted)));
    }
  }, [data]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setDifficulties((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update order based on new positions
        return newItems.map((item, index) => ({
          ...item,
          order: index + 1,
        }));
      });
    }
  };

  const handleCellDoubleClick = (id: string, field: "points" | "label" | "code") => {
    setEditingCell({ id, field });
  };

  const handleCellChange = (id: string, field: "points" | "label" | "code", value: string) => {
    setDifficulties((prev) =>
      prev.map((difficulty) =>
        difficulty.id === id
          ? {
              ...difficulty,
              [field]: value,
            }
          : difficulty
      )
    );
  };

  const handleCellBlur = () => {
    setEditingCell(null);
  };

  const handleAddRow = () => {
    const newId = `new-${Date.now()}`;
    const newDifficulty: Difficulty = {
      id: newId,
      code: "",
      label: "",
      points: 0,
      order: difficulties.length + 1,
      isActive: true,
      updatedAt: new Date().toISOString(),
    };
    setDifficulties([...difficulties, newDifficulty]);
    setNewRows((prev) => new Set(prev).add(newId));
  };


  const handleToggleActive = async (id: string) => {
    if (newRows.has(id)) {
      // For new rows, just toggle the local state
      setDifficulties((prev) =>
        prev.map((d) =>
          d.id === id ? { ...d, isActive: !d.isActive } : d
        )
      );
    } else {
      // For existing rows, call the API
      const difficulty = difficulties.find(d => d.id === id);
      if (!difficulty) return;

      const action = difficulty.isActive ? "비활성화" : "활성화";
      if (!window.confirm(`이 난이도를 ${action}하시겠습니까?`)) {
        return;
      }

      try {
        if (difficulty.isActive) {
          await deactivateDifficulty({
            variables: { id },
          });
        } else {
          // Assuming there's an activate mutation, otherwise use update
          await updateDifficulty({
            variables: {
              id,
              input: {
                isActive: true,
              },
            },
          });
        }
        alert(`난이도가 ${action}되었습니다.`);
        await refetch();
      } catch (error) {
        console.error("Toggle active error:", error);
        alert(`난이도 ${action}에 실패했습니다.`);
      }
    }
  };

  const handleReset = () => {
    setDifficulties(JSON.parse(JSON.stringify(originalDifficulties)));
    setEditingCell(null);
    setNewRows(new Set());
  };

  const handleSaveAll = async () => {
    try {
      // Validate all difficulties before saving
      for (const difficulty of difficulties) {
        // Check required fields
        if (!difficulty.code.trim()) {
          alert("코드를 입력해주세요.");
          return;
        }
        if (!difficulty.label.trim()) {
          alert("난이도명을 입력해주세요.");
          return;
        }

        const pointsValue = typeof difficulty.points === 'string' ? difficulty.points.trim() : difficulty.points;
        if (pointsValue === '' || pointsValue === null || pointsValue === undefined) {
          alert(`${difficulty.label}의 포인트를 입력해주세요.`);
          return;
        }
        const pointsNumber = typeof pointsValue === 'string' ? parseInt(pointsValue) : pointsValue;
        if (isNaN(pointsNumber) || pointsNumber < 0) {
          alert(`${difficulty.label}의 포인트는 0 이상의 숫자여야 합니다.`);
          return;
        }
      }

      // Create new difficulties
      for (const difficulty of difficulties) {
        if (newRows.has(difficulty.id)) {
          const pointsNumber = typeof difficulty.points === 'string'
            ? parseInt(difficulty.points.trim())
            : difficulty.points;

          await createDifficulty({
            variables: {
              input: {
                code: difficulty.code,
                label: difficulty.label,
                points: pointsNumber,
                order: difficulty.order,
              },
            },
          });
        }
      }

      // Update existing difficulties
      const updates = difficulties.map(async (difficulty) => {
        if (newRows.has(difficulty.id)) return; // Skip new rows (already created)

        const original = originalDifficulties.find(d => d.id === difficulty.id);
        if (!original) return;

        const hasLabelChange = difficulty.label !== original.label;
        const hasPointsChange = difficulty.points !== original.points;
        const hasOrderChange = difficulty.order !== original.order;

        if (hasLabelChange || hasPointsChange || hasOrderChange) {
          // Convert points to number for mutation
          const pointsNumber = typeof difficulty.points === 'string'
            ? parseInt(difficulty.points.trim())
            : difficulty.points;

          await updateDifficulty({
            variables: {
              id: difficulty.id,
              input: {
                label: hasLabelChange ? difficulty.label : undefined,
                points: hasPointsChange ? pointsNumber : undefined,
                order: hasOrderChange ? difficulty.order : undefined,
              },
            },
          });
        }
      });

      await Promise.all(updates);

      alert("난이도가 저장되었습니다.");
      await refetch();
      setEditingCell(null);
      setNewRows(new Set());
    } catch (error) {
      console.error("Save error:", error);
      alert("난이도 저장에 실패했습니다.");
    }
  };

  const hasChanges = () => {
    return JSON.stringify(difficulties) !== JSON.stringify(originalDifficulties) || newRows.size > 0;
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold">난이도 관리</h1>
        <div className="relative group">
          <div className="p-1 rounded-full hover:bg-gray-100 transition-colors cursor-help">
            <InformationCircleIcon className="w-5 h-5 text-gray-500" />
          </div>
          <div className="absolute left-0 top-full mt-2 w-80 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
            <p>- 코드, 난이도명, 기본 포인트는 셀을 더블클릭해서 수정 가능합니다.</p>
            <p>- 상태는 텍스트를 클릭하여 활성/비활성 상태를 변경할 수 있습니다.</p>
            <p>- 순서 셀을 클릭 후 드래그하여 조회 순서를 변경할 수 있습니다.</p>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex w-fit items-center rounded-t-lg border border-black bg-black py-2">
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: "80px", minWidth: "80px" }}
          >
            순서
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: "140px", minWidth: "140px" }}
          >
            코드
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: "180px", minWidth: "180px" }}
          >
            난이도명
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: "120px", minWidth: "120px" }}
          >
            기본 포인트
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: "80px", minWidth: "80px" }}
          >
            상태
          </div>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: "120px", minWidth: "120px" }}
          >
            수정일자
          </div>
        </div>

        <div className="w-fit border border-t-0 border-zinc-300 bg-white">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={difficulties.map(d => d.id)}
              strategy={verticalListSortingStrategy}
            >
              {difficulties?.map((difficulty) => (
                <SortableRow
                  key={difficulty.id}
                  difficulty={difficulty}
                  editingCell={editingCell}
                  onCellDoubleClick={handleCellDoubleClick}
                  onCellChange={handleCellChange}
                  onCellBlur={handleCellBlur}
                  onToggleActive={handleToggleActive}
                  isNewRow={newRows.has(difficulty.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        <div className="mt-4 flex gap-2">
          <BasicButton
            text="초기화"
            variant="outlined"
            className="px-4 py-2"
            disabled={!hasChanges()}
            onClick={handleReset}
          />
          <BasicButton
            text="행추가"
            variant="primary"
            className="px-4 py-2"
            onClick={handleAddRow}
          />
          <BasicButton
            text="저장"
            variant="primary"
            className="px-4 py-2"
            disabled={!hasChanges()}
            onClick={handleSaveAll}
          />
        </div>
      </div>
    </PageWrapper>
  );
}
